// File: frontend/src/components/MonthlyRevenueBreakdown.jsx

import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

export default function MonthlyRevenueBreakdown({
    activeTab,
    onSelectTab,
    onClose,
    monthlyRevenue,
    language = 'en',
    t
}) {
    const [filterQuery, setFilterQuery] = useState('');
    const breakdown = monthlyRevenue?.breakdown || {};
    const {
        possibleClasses = [],
        realizedItems = [],
        lostItems = [],
        upcomingItems = [],
        categorySummary = {}
    } = breakdown;

    const filteredPossibleClasses = useMemo(() => {
        if (!filterQuery) return possibleClasses;
        const q = filterQuery.toLowerCase();
        return possibleClasses.filter(c => 
            (c.name && c.name.toLowerCase().includes(q)) ||
            (c.cram_school_name && c.cram_school_name.toLowerCase().includes(q)) ||
            (c.day && c.day.toLowerCase().includes(q))
        );
    }, [possibleClasses, filterQuery]);

    const filteredRealizedItems = useMemo(() => {
        if (!filterQuery) return realizedItems;
        const q = filterQuery.toLowerCase();
        return realizedItems.filter(item => 
            (item.class_name && item.class_name.toLowerCase().includes(q)) ||
            (item.student_name && item.student_name.toLowerCase().includes(q)) ||
            (item.date && item.date.includes(q))
        );
    }, [realizedItems, filterQuery]);

    const filteredLostItems = useMemo(() => {
        if (!filterQuery) return lostItems;
        const q = filterQuery.toLowerCase();
        return lostItems.filter(item => 
            (item.class_name && item.class_name.toLowerCase().includes(q)) ||
            (item.student_name && item.student_name.toLowerCase().includes(q)) ||
            (item.date && item.date.includes(q))
        );
    }, [lostItems, filterQuery]);

    const filteredUpcomingItems = useMemo(() => {
        if (!filterQuery) return upcomingItems;
        const q = filterQuery.toLowerCase();
        return upcomingItems.filter(item => 
            (item.class_name && item.class_name.toLowerCase().includes(q)) ||
            (item.student_name && item.student_name.toLowerCase().includes(q)) ||
            (item.date && item.date.includes(q)) ||
            (item.dayOfWeek && item.dayOfWeek.toLowerCase().includes(q))
        );
    }, [upcomingItems, filterQuery]);

    return (
        <div className="mt-5 p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-md animate-in fade-in slide-in-from-top-3 duration-300 space-y-4">
            {/* Header & Tab Selector Pill Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2">
                    <span className="text-xl">📊</span>
                    <div>
                        <h4 className="text-base font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                            {language === 'zh' ? '月度營收深入洞察與明細' : 'Monthly Revenue In-Depth Analysis'}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {language === 'zh' ? '點選下方標籤切換分析維度，檢視每堂課與學生的收支貢獻' : 'Switch between tabs to inspect class & session level revenue contributions'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex p-1 bg-gray-100 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-700 text-xs font-bold gap-1">
                        <button
                            type="button"
                            onClick={() => onSelectTab('possible')}
                            className={classNames('px-3 py-1.5 rounded-lg transition-all cursor-pointer', {
                                'bg-blue-600 text-white shadow-xs': activeTab === 'possible',
                                'text-gray-600 dark:text-gray-400 hover:text-gray-900': activeTab !== 'possible'
                            })}
                        >
                            {language === 'zh' ? '全月預期' : 'Possible'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelectTab('current')}
                            className={classNames('px-3 py-1.5 rounded-lg transition-all cursor-pointer', {
                                'bg-emerald-600 text-white shadow-xs': activeTab === 'current',
                                'text-gray-600 dark:text-gray-400 hover:text-gray-900': activeTab !== 'current'
                            })}
                        >
                            {language === 'zh' ? '實得課費' : 'Realized'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelectTab('lost')}
                            className={classNames('px-3 py-1.5 rounded-lg transition-all cursor-pointer', {
                                'bg-red-600 text-white shadow-xs': activeTab === 'lost',
                                'text-gray-600 dark:text-gray-400 hover:text-gray-900': activeTab !== 'lost'
                            })}
                        >
                            {language === 'zh' ? '缺課損失' : 'Lost'}
                        </button>
                        <button
                            type="button"
                            onClick={() => onSelectTab('remaining')}
                            className={classNames('px-3 py-1.5 rounded-lg transition-all cursor-pointer', {
                                'bg-gray-800 text-white shadow-xs': activeTab === 'remaining',
                                'text-gray-600 dark:text-gray-400 hover:text-gray-900': activeTab !== 'remaining'
                            })}
                        >
                            {language === 'zh' ? '剩餘未上' : 'Pipeline'}
                        </button>
                    </div>
                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 rounded-lg hover:bg-gray-100 cursor-pointer text-sm font-bold"
                            title={language === 'zh' ? '關閉明細' : 'Close breakdown'}
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Sub-header Filter Bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="relative w-full sm:w-64">
                    <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-400 dark:text-gray-500 text-xs">🔍</span>
                    <input
                        type="text"
                        value={filterQuery}
                        onChange={e => setFilterQuery(e.target.value)}
                        placeholder={language === 'zh' ? '搜尋班級、學生姓名或日期...' : 'Search class, student, or date...'}
                        className="w-full pl-7 pr-7 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {filterQuery && (
                        <button
                            type="button"
                            onClick={() => setFilterQuery('')}
                            className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 cursor-pointer"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* Category Metric Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                    {activeTab === 'possible' && (
                        <>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-800 border border-blue-200 font-semibold">
                                🏠 {language === 'zh' ? '個別家教預期' : 'Tutoring'}: NT${(categorySummary.tutoringPossible || 0).toLocaleString()}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-800 border border-purple-200 font-semibold">
                                🏫 {language === 'zh' ? '安親合約預期' : 'Cram Schools'}: NT${(categorySummary.cramPossible || 0).toLocaleString()}
                            </span>
                        </>
                    )}
                    {activeTab === 'current' && (
                        <>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                                🏠 {language === 'zh' ? '家教實得' : 'Tutoring Realized'}: NT${(categorySummary.tutoringRealized || 0).toLocaleString()}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-800 border border-purple-200 font-semibold">
                                🏫 {language === 'zh' ? '安親合約實得' : 'Cram Realized'}: NT${(categorySummary.cramRealized || 0).toLocaleString()}
                            </span>
                        </>
                    )}
                    {activeTab === 'lost' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 text-red-800 border border-red-200 font-semibold">
                            ⚠️ {language === 'zh' ? '請假未收總額' : 'Total Absent Unbilled'}: NT${monthlyRevenue.lostRevenue.toLocaleString()}
                        </span>
                    )}
                    {activeTab === 'remaining' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 font-semibold">
                            ⏳ {language === 'zh' ? `本月剩餘 ${monthlyRevenue.daysRemaining} 天` : `${monthlyRevenue.daysRemaining} Days Remaining`}
                        </span>
                    )}
                </div>
            </div>

            {/* TAB 1: POSSIBLE MONTHLY INCOME BREAKDOWN */}
            {activeTab === 'possible' && (
                <div className="space-y-3">
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 font-bold">
                                <tr>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '班級名稱' : 'Class Name'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '分類形式' : 'Type & Format'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '排班時段' : 'Schedule'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '在班人數' : 'Enrolled'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '全月堂數' : 'Month Sessions'}</th>
                                    <th className="py-2.5 px-3 text-right">{language === 'zh' ? '全月預期總收' : 'Projected Total'}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                {filteredPossibleClasses.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
                                            {language === 'zh' ? '無相符班級' : 'No matching classes found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPossibleClasses.map(cls => (
                                        <tr key={cls.id} className="hover:bg-gray-50/80 transition-colors">
                                            <td className="py-2.5 px-3 font-bold text-gray-900 dark:text-gray-100">
                                                <Link to={`/classes/${cls.id}`} className="text-blue-600 hover:underline">
                                                    {cls.name}
                                                </Link>
                                            </td>
                                            <td className="py-2.5 px-3">
                                                {cls.class_type === 'cram_school' ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-bold bg-purple-100 text-purple-800">
                                                        🏫 {cls.cram_school_name || 'Cram School'}
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-800">
                                                        🏠 Private
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400">
                                                {cls.day} {cls.start_time?.slice(0, 5)} - {cls.end_time?.slice(0, 5)}
                                            </td>
                                            <td className="py-2.5 px-3 text-center text-gray-700 dark:text-gray-300 font-medium">
                                                {cls.enrolledCount} {language === 'zh' ? '人' : 'students'}
                                            </td>
                                            <td className="py-2.5 px-3 text-center text-gray-700 dark:text-gray-300 font-bold">
                                                {cls.sessionsCount} {language === 'zh' ? '堂' : 'sessions'}
                                            </td>
                                            <td className="py-2.5 px-3 text-right font-extrabold text-blue-900">
                                                NT${cls.projectedTotal.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            <tfoot className="bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 font-extrabold text-gray-900 dark:text-gray-100">
                                <tr>
                                    <td colSpan="4" className="py-2.5 px-3">
                                        {language === 'zh' ? '全月排班應收總計' : 'Total Monthly Projection'}:
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        {filteredPossibleClasses.reduce((acc, c) => acc + c.sessionsCount, 0)} {language === 'zh' ? '堂' : 'sessions'}
                                    </td>
                                    <td className="py-2.5 px-3 text-right text-blue-950 text-sm">
                                        NT${monthlyRevenue.possibleMonthlyIncome.toLocaleString()}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 2: CURRENT DATE INCOME (REALIZED) BREAKDOWN */}
            {activeTab === 'current' && (
                <div className="space-y-3">
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 max-h-[400px]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 font-bold sticky top-0 z-10 shadow-2xs">
                                <tr>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '上課日期' : 'Date'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '班級名稱' : 'Class'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '學生 / 項目' : 'Student / Target'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '點名狀態' : 'Status'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '收款狀態' : 'Payment'}</th>
                                    <th className="py-2.5 px-3 text-right">{language === 'zh' ? '實得金額' : 'Realized NTD'}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                {filteredRealizedItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
                                            {language === 'zh' ? '截至今日尚無點名已上課之記錄' : 'No realized class sessions recorded yet.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRealizedItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                            <td className="py-2.5 px-3 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                {item.date}
                                            </td>
                                            <td className="py-2.5 px-3 font-bold text-gray-900 dark:text-gray-100">
                                                {item.class_name}
                                                {item.class_type === 'cram_school' && (
                                                    <span className="text-[10px] text-purple-700 block font-normal">
                                                        🏫 {item.cram_school_name || 'Cram School'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-2.5 px-3 text-gray-800 dark:text-gray-200">
                                                {item.student_name}
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800">
                                                    ✓ {item.status === 'held' ? (language === 'zh' ? '已上課' : 'Held') : (item.status === 'late' ? (language === 'zh' ? '遲到' : 'Late') : (language === 'zh' ? '出席' : 'Present'))}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold', {
                                                    'bg-green-100 text-green-800': item.fee_status === 'paid',
                                                    'bg-amber-100 text-amber-800': item.fee_status !== 'paid'
                                                })}>
                                                    {item.fee_status === 'paid' ? (language === 'zh' ? '✓ 已入帳' : 'Paid') : (language === 'zh' ? '⏳ 待結' : 'Pending')}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-right font-extrabold text-emerald-700">
                                                NT${item.amount.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 3: LOST REVENUE ANALYSIS & MAKE-UP OPPORTUNITY */}
            {activeTab === 'lost' && (
                <div className="space-y-3">
                    <div className="p-3.5 bg-amber-50/80 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2.5">
                        <span className="text-xl">💡</span>
                        <div>
                            <strong className="font-bold">{language === 'zh' ? '補課補償契機：' : 'Make-Up Class Recovery Opportunity:'}</strong>
                            <p className="mt-0.5">
                                {language === 'zh' 
                                    ? '個別家教學生請假時短收之費用，可藉由為學生安排「單次補課 (One-off Make-up Class)」全數補回！補課完成點名後，該金額即會直接轉移為「實得課費」，消除缺課損失。'
                                    : 'Fees lost due to student absences can be recovered 100% by scheduling a one-off make-up class. Once attendance for the make-up session is recorded, it moves directly into Realized Income.'}
                            </p>
                        </div>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 font-bold">
                                <tr>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '缺課日期' : 'Date'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '班級' : 'Class'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '學生姓名' : 'Student Name'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '短收原因' : 'Reason'}</th>
                                    <th className="py-2.5 px-3 text-right">{language === 'zh' ? '短收損失' : 'Lost Amount'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '立即行動' : 'Action'}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                {filteredLostItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-8 text-center text-emerald-600 font-bold">
                                            🎉 {language === 'zh' ? '本月全體學生全勤！目前無任何缺課損失。' : 'No lost revenue recorded! 100% completion across all classes.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredLostItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-red-50/50 transition-colors">
                                            <td className="py-2.5 px-3 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                {item.date}
                                            </td>
                                            <td className="py-2.5 px-3 font-bold text-gray-900 dark:text-gray-100">
                                                {item.class_name}
                                            </td>
                                            <td className="py-2.5 px-3 font-semibold text-gray-800 dark:text-gray-200">
                                                {item.student_name}
                                            </td>
                                            <td className="py-2.5 px-3 text-red-600 font-medium">
                                                {item.reason}
                                            </td>
                                            <td className="py-2.5 px-3 text-right font-extrabold text-red-600">
                                                -NT${item.amount.toLocaleString()}
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <Link
                                                    to="/attendance"
                                                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-xs font-bold transition-colors cursor-pointer shadow-2xs"
                                                >
                                                    <span>📅</span>
                                                    <span>{language === 'zh' ? '安排補課' : 'Schedule Make-Up'}</span>
                                                </Link>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB 4: REMAINING PROJECTED PIPELINE */}
            {activeTab === 'remaining' && (
                <div className="space-y-3">
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 max-h-[400px]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-600 dark:text-gray-400 font-bold sticky top-0 z-10 shadow-2xs">
                                <tr>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '排定日期' : 'Date'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '星期' : 'Day'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '班級名稱' : 'Class'}</th>
                                    <th className="py-2.5 px-3 text-left">{language === 'zh' ? '學生 / 對象' : 'Target / Student'}</th>
                                    <th className="py-2.5 px-3 text-center">{language === 'zh' ? '預期狀態' : 'Status'}</th>
                                    <th className="py-2.5 px-3 text-right">{language === 'zh' ? '預期收入' : 'Expected NTD'}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                {filteredUpcomingItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
                                            {language === 'zh' ? '本月剩餘日程已無排定課堂' : 'No remaining scheduled sessions this month.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUpcomingItems.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/80 transition-colors">
                                            <td className="py-2.5 px-3 font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                {item.date}
                                            </td>
                                            <td className="py-2.5 px-3 text-gray-600 dark:text-gray-400">
                                                {item.dayOfWeek}
                                            </td>
                                            <td className="py-2.5 px-3 font-bold text-gray-900 dark:text-gray-100">
                                                {item.class_name}
                                            </td>
                                            <td className="py-2.5 px-3 text-gray-800 dark:text-gray-200">
                                                {item.student_name || `${item.enrolledCount} 在班`}
                                            </td>
                                            <td className="py-2.5 px-3 text-center">
                                                <span className={classNames('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold', {
                                                    'bg-amber-100 text-amber-800': item.status === 'pending_rollcall',
                                                    'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300': item.status !== 'pending_rollcall'
                                                })}>
                                                    {item.status === 'pending_rollcall' 
                                                        ? (language === 'zh' ? '⏳ 待點名' : 'Pending Roll Call') 
                                                        : (language === 'zh' ? '📅 即將上課' : 'Scheduled')}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-right font-extrabold text-gray-800 dark:text-gray-200">
                                                NT${item.amount.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
